/**
 * Line Type Enum
 *
 * Represents the different types of mooring lines used in vessel berthing.
 * Values are 1-based to maintain compatibility with the backend API.
 *
 * Line type order determines:
 * - Visual sorting in tables and displays
 * - Grouping into categories (bowStern, breast, spring)
 * - Mapping to fairlead paths and bollard assignments
 */
export enum LineType {
  BowLine = 1,
  ForeBreastLine = 2,
  ForeSpringLine = 3,
  AftSpringLine = 4,
  AftBreastLine = 5,
  SternLine = 6,
}

/**
 * Line type display names (full)
 * Maps to i18n keys: components.ma.scenarioEditor.fields.mooring.lines.long.*
 */
export const lineTypeIdList: readonly string[] = [
  'bowLine',
  'foreBreastLine',
  'foreSpringLine',
  'aftSpringLine',
  'aftBreastLine',
  'sternLine',
] as const;

/**
 * Line type display names (short)
 * Maps to i18n keys: components.ma.scenarioEditor.fields.mooring.lines.short.*
 */
export const lineTypeIdShortList: readonly string[] = [
  'bow',
  'foreBreast',
  'foreSpring',
  'aftSpring',
  'aftBreast',
  'stern',
] as const;

/**
 * Line type grouping keys
 * Used for fairlead path selection and logical grouping
 */
export const lineGroupKeys: readonly string[] = [
  'bowStern',
  'breast',
  'spring',
  'spring',
  'breast',
  'bowStern',
] as const;

/**
 * Type guard to check if a value is a valid LineType
 */
export function isLineType(value: any): value is LineType {
  return (
    typeof value === 'number' &&
    value >= LineType.BowLine &&
    value <= LineType.SternLine &&
    Number.isInteger(value)
  );
}

/**
 * Helper utilities for working with LineType enum
 */
export const LineTypeHelpers = {
  /**
   * Convert LineType to array index (0-based)
   * @param type - The line type
   * @returns Array index (0-5)
   */
  toIndex(type: LineType): number {
    return type - 1;
  },

  /**
   * Get the group key for a line type
   * @param type - The line type
   * @returns Group key: 'bowStern', 'breast', or 'spring'
   */
  toGroupKey(type: LineType): string {
    return lineGroupKeys[type - 1];
  },

  /**
   * Get the full line type ID
   * @param type - The line type
   * @returns Full name: 'bowLine', 'foreBreastLine', etc.
   */
  toLineTypeId(type: LineType): string {
    return lineTypeIdList[type - 1];
  },

  /**
   * Get the short line type ID
   * @param type - The line type
   * @returns Short name: 'bow', 'foreBreast', etc.
   */
  toShortName(type: LineType): string {
    return lineTypeIdShortList[type - 1];
  },

  /**
   * Get all line types as an array
   * @returns Array of all LineType values
   */
  all(): LineType[] {
    return [
      LineType.BowLine,
      LineType.ForeBreastLine,
      LineType.ForeSpringLine,
      LineType.AftSpringLine,
      LineType.AftBreastLine,
      LineType.SternLine,
    ];
  },

  /**
   * Convert a numeric value to LineType with validation
   * @param value - Numeric value to convert
   * @returns LineType if valid, undefined otherwise
   */
  fromNumber(value: number): LineType | undefined {
    return isLineType(value) ? value : undefined;
  },
};
